import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteLabRegistration, GetLabRegistrations } from "../../../../api/diagnosis/transaction/labRegistrationAPI";

const ListLabRegistrationMultiples = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchLabRegistrationMultiples = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetLabRegistrations();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          lab: item.lab?.name || "",
          labNo: item.labNo || "",
          type: item.type || "",

          date: item.date
            ? new Date(item.date).toLocaleDateString()
            : "",
          time: item.time || "",

          patientName: item.patientName || "",
          guardianName: item.guardianName || "",
          mobileNo: item.mobileNo || "",
          sex: item.sex || "",
          age: `${item.age || ""} ${item.ageSuffix || ""}`,

          area: item.areaId?.name || "",
          collectionCenter: item.collactionCenter || "",

          totalTests: item.testDetails?.length || 0,
          totalDepartments: item.combinedDetails?.length || 0,

          referenceDoctor: item.referenceDoctorId?.name || "",
          consultantDoctor: item.consultantDoctorId?.name || "",

          total: item.total ?? 0,
          advance: item.advance ?? 0,
          balance: item.balance ?? 0,
          paymentMode: item.paymentMode || ""
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchLabRegistrationMultiples();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(
      `/diagnosis/transaction/lab-registration-multiple/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This lab registration will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteLabRegistration(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev =>
              prev.filter(item => item.id !== id)
            );
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },

    { name: "Lab No", selector: row => row.labNo, sortable: true, width: "10%" },
    { name: "Type", selector: row => row.type, width: "10%" },

    { name: "Date", selector: row => row.date, width: "10%" },
    { name: "Time", selector: row => row.time, width: "10%" },

    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "10%" },
    { name: "Guardian", selector: row => row.guardianName, width: "10%" },
    { name: "Mobile", selector: row => row.mobileNo, width: "10%" },
    { name: "Age / Sex", selector: row => `${row.age} / ${row.sex}`, width: "10%" },

    { name: "Area", selector: row => row.area, width: "10%" },
    { name: "Collection Center", selector: row => row.collectionCenter, width: "10%" },

    { name: "Tests", selector: row => row.totalTests, width: "10%" },
    { name: "Departments", selector: row => row.totalDepartments, width: "10%" },

    { name: "Reference Doctor", selector: row => row.referenceDoctor, width: "10%" },
    { name: "Consultant Doctor", selector: row => row.consultantDoctor, width: "10%" },

    { name: "Total", selector: row => row.total, width: "10%" },
    { name: "Advance", selector: row => row.advance, width: "10%" },
    { name: "Balance", selector: row => row.balance, width: "10%" },

    { name: "Payment Mode", selector: row => row.paymentMode, width: "10%" },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%"
    }
  ];

  const headers = [
    { label: "Lab No", key: "labNo" },
    { label: "Type", key: "type" },
    { label: "Date", key: "date" },
    { label: "Time", key: "time" },
    { label: "Patient Name", key: "patientName" },
    { label: "Guardian", key: "guardianName" },
    { label: "Mobile", key: "mobileNo" },
    { label: "Age", key: "age" },
    { label: "Sex", key: "sex" },
    { label: "Area", key: "area" },
    { label: "Collection Center", key: "collectionCenter" },
    { label: "Total Tests", key: "totalTests" },
    { label: "Total Departments", key: "totalDepartments" },
    { label: "Reference Doctor", key: "referenceDoctor" },
    { label: "Consultant Doctor", key: "consultantDoctor" },
    { label: "Total", key: "total" },
    { label: "Advance", key: "advance" },
    { label: "Balance", key: "balance" },
    { label: "Payment Mode", key: "paymentMode" }
  ];

  return (
    <Fragment>
      <Breadcrumb parent="Lab" title="Lab Registration List" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Lab Registration Multiples</h5>
              <Link
                to={`/diagnosis/transaction/lab-registration-multiple/add`}
              >
                <button className="btn btn-primary">
                  Create Lab Registration
                </button>
              </Link>
            </div>

            <div className="row mt-3">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="col-sm-6 text-end">
                <CSVLink
                  data={data}
                  headers={headers}
                  filename="lab-registration-multiples.csv"
                  className="btn btn-success"
                >
                  Export CSV
                </CSVLink>
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListLabRegistrationMultiples;